import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import StatsCard from '../components/dashboard/StatsCard';

const useQuery = () => {
    return new URLSearchParams(window.location.search);
}

const DriverSpecificSales = () => {
    const { isDark } = useTheme();
    const query = useQuery();
    const driverId = query.get('driver');
    const [stats, setStats] = useState(null);
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (driverId) {
            fetchData();
        }
    }, [driverId]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [statsResponse, salesResponse] = await Promise.all([
                fetch(`/api/v1/driver_stats/${driverId}`),
                fetch(`/api/v1/sales?driver_id=${driverId}`)
            ]);

            const statsData = await statsResponse.json();
            const salesData = await salesResponse.json();

            setStats(statsData);
            setSales(salesData);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const styles = {
        container: {
            padding: '2rem',
            background: isDark ? '#0a0a0a' : '#f8f9fa',
            minHeight: '100vh',
            color: isDark ? '#fff' : '#1f2937'
        },
        header: {
            marginBottom: '2rem'
        },
        title: {
            fontSize: '1.8rem',
            fontWeight: 'bold',
            marginBottom: '0.5rem'
        },
        subTitle: {
            fontSize: '1.25rem',
            color: isDark ? '#9ca3af' : '#6b7280',
            marginBottom: '1rem'
        },
        statsGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '1.5rem',
            marginBottom: '2rem'
        },
        tableContainer: {
            background: isDark ? '#1e1e1e' : '#ffffff',
            borderRadius: '1rem',
            boxShadow: isDark
                ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
                : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            overflowX: 'auto',
            padding: '1rem'
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
        },
        th: {
            textAlign: 'left',
            padding: '1rem',
            borderBottom: `2px solid ${isDark ? '#333' : '#e5e7eb'}`,
            fontSize: '0.75rem',
            fontWeight: 600,
            color: isDark ? '#9ca3af' : '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            whiteSpace: 'nowrap'
        },
        td: {
            padding: '1rem',
            borderBottom: `1px solid ${isDark ? '#333' : '#e5e7eb'}`,
            color: isDark ? '#d1d5db' : '#374151',
            fontSize: '0.875rem',
            whiteSpace: 'nowrap'
        },
        statusBadge: (status) => ({
            display: 'inline-block',
            padding: '0.25rem 0.75rem',
            borderRadius: '9999px',
            fontSize: '0.75rem',
            fontWeight: 600,
            background: status === 'RECEIVED' ? 'rgba(16, 185, 129, 0.2)' :
                status === 'SENT_BY_DRIVER' ? 'rgba(59, 130, 246, 0.2)' :
                    'rgba(245, 158, 11, 0.2)',
            color: status === 'RECEIVED' ? '#10b981' :
                status === 'SENT_BY_DRIVER' ? '#3b82f6' :
                    '#f59e0b',
        }),
        loading: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            fontSize: '1.5rem'
        }
    };

    if (loading) return <div style={styles.loading}>Loading sales data...</div>;
    if (!stats) return <div style={styles.loading}>Driver not found</div>;

    const { driver, car, metrics } = stats;
    // model_blob is inside car, we need to handle if it's parsed or not based on how rails returns it.
    // Usually via as_json it returns the json object.
    const currentYear = new Date().getFullYear().toString();
    console.log("Current Year:", currentYear);
    console.log("Car Model Blob Sales:", car?.model_blob?.sales);

    let weeklyTarget = car?.model_blob?.sales || 'N/A';
    if (typeof weeklyTarget === 'object' && weeklyTarget !== null) {
        weeklyTarget = weeklyTarget[currentYear] || Object.values(weeklyTarget)[0] || 'N/A';
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>
                    {driver.first_name} {driver.last_name}
                </h1>
                <div style={styles.subTitle}>
                    {car?.model} - {car?.car_number}
                </div>
                {(car?.image_url || car?.car_image_link) && (
                    <div style={{ marginBottom: '1.5rem' }}>
                        <img
                            src={car.image_url || car.car_image_link}
                            alt={`${car.model} - ${car.car_number}`}
                            style={{
                                width: '100px',
                                height: '100px',
                                borderRadius: '1rem',
                                objectFit: 'cover',
                                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                            }}
                        />
                    </div>
                )}
            </div>

            <div style={styles.statsGrid}>
                <StatsCard
                    title="Total Sales"
                    value={`GHS ${parseFloat(metrics.all_time_sales).toLocaleString()}`}
                    icon="💰"
                    color="green"
                />
                <StatsCard
                    title="Weekly Target"
                    value={weeklyTarget}
                    icon="🎯"
                    color="blue"
                />
            </div>

            <h2 style={{ ...styles.title, fontSize: '1.5rem', marginBottom: '1rem' }}>Sales History</h2>

            <div style={styles.tableContainer}>
                {sales.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>No sales records found</div>
                ) : (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Received Date</th>
                                <th style={styles.th}>Expected Date</th>
                                <th style={styles.th}>Total Days</th>
                                <th style={styles.th}>Amount</th>
                                <th style={styles.th}>Expected Amount</th>
                                <th style={styles.th}>Payment Mode</th>
                                <th style={styles.th}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sales.map((sale) => (
                                <tr key={sale.id}>
                                    <td style={styles.td}>{sale.date_received}</td>
                                    <td style={styles.td}>{sale.expected_date || '-'}</td>
                                    <td style={{ ...styles.td, textAlign: 'center' }}>{sale.days_sales_amount_covers || '-'}</td>
                                    <td style={{ ...styles.td, fontWeight: 'bold' }}>GHS {parseFloat(sale.amount).toFixed(2)}</td>
                                    <td style={styles.td}>{sale.amount_expected ? `GHS ${parseFloat(sale.amount_expected).toFixed(2)}` : '-'}</td>
                                    <td style={styles.td}>{sale.payment_mode || '-'}</td>
                                    <td style={styles.td}>
                                        <span style={styles.statusBadge(sale.status)}>
                                            {sale.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default DriverSpecificSales;

